import { CaseDetail } from "../../models/CaseDetail/caseDetail.model";
import { CaseStatusEnum } from "../../enums/caseStatus.enum";
import { UserRoleEnum } from "../../enums/userRole.enum";

export interface CosignConfirmConfig {
  /** Confirmation prompt to show inside the Popconfirm/Modal. */
  message: string;
  /** Primary button label. */
  okText: string;
}

export interface CosignConfirmInput {
  caseDetail:
    | Pick<
        CaseDetail,
        | "status"
        | "cosignEnabled"
        | "assignedToDoctorId"
        | "assignedToDoctorName"
        | "assignedToAdminId"
      >
    | undefined
    | null;
  userRole: UserRoleEnum | undefined;
  userId: number | string | undefined;
  /** Doctors with the typist feature should not be prompted (they're working as scribes for an assigned doctor). */
  isTypist?: boolean;
  isCosignEnabled?: boolean;
}

const sameId = (
  a: number | string | undefined,
  b: number | string | undefined
) => {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  return String(a) === String(b);
};

/**
 * Returns a confirmation config when opening the Generate Report flow for
 * this case would silently take ownership via cosign_assign — i.e. the case
 * is in cosign mode and clicking Generate Report would assign it to the
 * current user without an explicit step. Returns `null` when no popup is
 * needed (case isn't in cosign mode, is already mine, is REPORTED, or is
 * TO_BE_APPROVED — admins approving don't need a popup, doctors don't see
 * the button there at all).
 */
export function getCosignConfirm({
  caseDetail,
  userRole,
  userId,
  isTypist,
  isCosignEnabled = false,
}: CosignConfirmInput): CosignConfirmConfig | null {
  if (!isCosignEnabled || !caseDetail) return null;

  // Generate Report button is hidden for REPORTED, and admins opening
  // TO_BE_APPROVED are approving (not reopening) — no popup in either case.
  if (caseDetail.status === CaseStatusEnum.REPORTED) return null;
  if (caseDetail.status === CaseStatusEnum.TO_BE_APPROVED) return null;

  if (caseDetail.status === CaseStatusEnum.ASSIGNED) {
    const assignedToMe =
      userRole === UserRoleEnum.ADMIN
        ? sameId(userId, caseDetail.assignedToAdminId)
        : sameId(userId, caseDetail.assignedToDoctorId);
    if (assignedToMe) return null;
    if (isTypist) return null;

    const otherDoctor =
      caseDetail.assignedToDoctorName?.trim() || "another doctor";
    return {
      message: `Case being reported by ${otherDoctor}. Do you want to open the case to edit under your name?`,
      okText: "Yes, open under my name",
    };
  }

  // Other non-assigned states (UNASSIGNED, etc.) — generic confirmation.
  return {
    message: "This will assign the case to you. Are you sure you want to continue?",
    okText: "Yes, assign to me",
  };
}
